import { queryOptions } from "@tanstack/react-query";

import type { OffboardingReview } from "@/lib/offboarding-review";

export interface OffboardingDashboardRequest {
  id: string;
  jitbitTicketId: number;
  status: string;
  parsedFirstName: string | null;
  parsedLastName: string | null;
  parsedEmployeeNumber: string | null;
  parsedRaison: string | null;
  snipeitSearchKey: string | null;
  snipeitUserName: string | null;
  initialDecision: string | null;
  receivedAt: string | null;
  updatedAt: string | null;
}

export interface OffboardingRequestDetail extends OffboardingDashboardRequest {
  review: OffboardingReview;
}

export interface StackMatch {
  adUser: {
    email: string;
    name: string;
    samAccountName: string;
    lastLogonDate: string | null;
    enabled: boolean;
    title: string | null;
    employeeID: string | null;
  } | null;
  stackUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    projectRole: string;
    isActive: boolean;
    isEmployee: boolean;
  };
}

export interface StackVerifyResponse {
  metadata: {
    totalADUsers: number;
    totalStackUsers: number;
    matchingUsersCount: number;
    specialUsersExcluded: number;
    timestamp: string;
  };
  matches: StackMatch[];
  specialUsers: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    projectRole: string;
  }>;
}

/** Offboarding requests waiting for an admin action (GET /api/offboarding/requests). */
export const offboardingRequestsQueryOptions = queryOptions({
  queryKey: ["admin", "offboarding", "requests"],
  queryFn: async (): Promise<OffboardingDashboardRequest[]> => {
    const res = await fetch("/api/offboarding/requests", { cache: "no-store" });

    if (!res.ok) {
      // SAFETY: the API error payload is { message?: string }; the throw below
      // falls back to the HTTP status text.
      const data = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(data?.message || `Erreur ${res.status}`);
    }

    // SAFETY: GET /api/offboarding/requests resolves to { requests: [...] }.
    const data = (await res.json()) as { requests: OffboardingDashboardRequest[] };

    return data.requests ?? [];
  },
});

/** One request with what a decision needs (GET /api/offboarding/requests/[id]). */
export const offboardingRequestQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["admin", "offboarding", "request", id],
    retry: false,
    queryFn: async (): Promise<OffboardingRequestDetail> => {
      const res = await fetch(`/api/offboarding/requests/${id}`, { cache: "no-store" });

      if (!res.ok) {
        // SAFETY: the route's error envelope carries an optional message.
        const failure = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(failure?.message || `Erreur ${res.status}`);
      }

      // SAFETY: GET /api/offboarding/requests/[id] answers { request } on success.
      const data = (await res.json().catch(() => null)) as {
        request?: OffboardingRequestDetail;
        message?: string;
      } | null;

      if (!data?.request) throw new Error("La demande n'a pas pu être chargée.");

      return data.request;
    },
  });

/** Stale-account analysis of the AD directory against the stack (verify-stale). */
export const stackStaleVerifyQueryOptions = queryOptions({
  queryKey: ["admin", "stack-stale", "verify"],
  queryFn: async (): Promise<StackVerifyResponse> => {
    const res = await fetch("/api/admin/stack/verify-stale", { cache: "no-store" });

    if (!res.ok) {
      // SAFETY: /api/admin/stack/verify-stale errors use the { message?: string }
      // envelope; a non-JSON body is caught to null.
      const data = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(
        data?.message || "Une erreur est survenue lors de la récupération des données",
      );
    }

    // SAFETY: /api/admin/stack/verify-stale returns the StackVerifyResponse contract.
    return (await res.json()) as StackVerifyResponse;
  },
});
